import { useState, useEffect, useRef } from 'react';
import { useChatFlow, Message, QuickReply } from './useChatFlow';

const BRAND = '#40BFB9';

function TypingIndicator() {
  return (
    <div className="flex gap-1 px-4 py-3">
      <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
      <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
      <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
    </div>
  );
}

function QuickReplies({ replies, onSelect }: { replies: QuickReply[]; onSelect: (r: QuickReply) => void }) {
  return (
    <div className="flex flex-wrap gap-2 px-4 pb-3">
      {replies.map((r) => (
        <button
          key={r.value}
          onClick={() => onSelect(r)}
          className="px-3 py-1.5 text-sm font-medium rounded-full border transition-all hover:shadow-md"
          style={{ borderColor: BRAND, color: BRAND, background: 'white' }}
          onMouseEnter={(e) => { e.currentTarget.style.background = BRAND; e.currentTarget.style.color = 'white'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = 'white'; e.currentTarget.style.color = BRAND; }}
        >
          {r.label}
        </button>
      ))}
    </div>
  );
}

function renderMarkdown(text: string): string {
  return text
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\n/g, '<br/>');
}

function ChatMessage({ msg }: { msg: Message }) {
  const isUser = msg.role === 'user';
  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'} px-4 py-1`}>
      <div
        className={`max-w-[80%] px-3.5 py-2.5 text-sm leading-relaxed rounded-2xl ${
          isUser ? 'rounded-br-md text-white' : 'rounded-bl-md bg-gray-100 text-gray-800'
        }`}
        style={isUser ? { background: BRAND } : undefined}
        dangerouslySetInnerHTML={{ __html: renderMarkdown(msg.text) }}
      />
    </div>
  );
}

export default function ChatWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [input, setInput] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const {
    messages, isTyping, isInputDisabled, chatState, handleQuickReply, handleUserInput, startConversation, resetConversation,
  } = useChatFlow();

  useEffect(() => {
    if (isOpen && messages.length === 0) startConversation();
  }, [isOpen, messages.length, startConversation]);

  // Lock background scroll on mobile while the chat widget is open.
  useEffect(() => {
    if (!isOpen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping]);

  useEffect(() => {
    if (isOpen && !isInputDisabled) inputRef.current?.focus();
  }, [isOpen, isInputDisabled]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || isInputDisabled) return;
    handleUserInput(input.trim());
    setInput('');
  };

  const getPlaceholder = () => {
    if (isInputDisabled) return 'Choose an option above...';
    if (chatState === 'awaiting_ticket_description') return 'Describe your issue or request...';
    if (chatState === 'awaiting_email') return 'Enter your email address...';
    if (chatState === 'awaiting_free_text') return 'Type your question...';
    return 'Type your message...';
  };

  return (
    <>
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-transform hover:scale-110"
          style={{ background: BRAND }}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          </svg>
        </button>
      )}

      {isOpen && (
        <div className="fixed inset-x-4 bottom-4 h-[55vh] sm:inset-x-auto sm:bottom-6 sm:right-6 z-50 sm:w-[380px] sm:h-[560px] w-auto max-w-[380px] mx-auto sm:mx-0 bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden border border-gray-200">
          <div className="flex items-center justify-between px-4 py-3" style={{ background: `linear-gradient(135deg, ${BRAND}, #1e3a5f)` }}>
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <path d="M8 14s1.5 2 4 2 4-2 4-2" />
                  <line x1="9" y1="9" x2="9.01" y2="9" />
                  <line x1="15" y1="9" x2="15.01" y2="9" />
                </svg>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-white">Netsights Assistant</h3>
                <p className="text-[10px] text-white/70">Ask anything about our platform</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button onClick={resetConversation} className="p-1.5 rounded-full hover:bg-white/10 transition-colors" title="Reset conversation">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                  <polyline points="23 4 23 10 17 10" />
                  <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
                </svg>
              </button>
              <button onClick={() => setIsOpen(false)} className="p-1.5 rounded-full hover:bg-white/10 transition-colors">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto py-3 space-y-1">
            {messages.map((msg) => (
              <div key={msg.id}>
                <ChatMessage msg={msg} />
                {msg.quickReplies && (
                  <QuickReplies replies={msg.quickReplies} onSelect={handleQuickReply} />
                )}
              </div>
            ))}
            {isTyping && <TypingIndicator />}
            <div ref={messagesEndRef} />
          </div>

          <form onSubmit={handleSubmit} className="flex items-center gap-2 px-4 py-3 border-t border-gray-100 bg-white">
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={getPlaceholder()}
              disabled={isInputDisabled}
              className="flex-1 px-3.5 py-2 text-sm rounded-full border border-gray-200 outline-none focus:border-teal-400 transition-colors disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed"
            />
            <button
              type="submit"
              disabled={isInputDisabled || !input.trim()}
              className="w-9 h-9 rounded-full flex items-center justify-center transition-all disabled:opacity-40"
              style={{ background: BRAND }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5">
                <line x1="22" y1="2" x2="11" y2="13" />
                <polygon points="22 2 15 22 11 13 2 9 22 2" />
              </svg>
            </button>
          </form>
        </div>
      )}
    </>
  );
}
